import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useShop } from "@/contexts/ShopContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { VisibilityChart } from "@/components/RankHistoryChart";
import GscPanel from "@/components/GscPanel";
import AlertsPanel from "@/components/AlertsPanel";
import RetestAllButton from "@/components/RetestAllButton";
import { Button } from "@/components/ui/button";
import { DASH } from "@/constants/testIds";
import {
  Target,
  ArrowUpDown,
  Eye,
  Users,
  TrendingUp,
  TrendingDown,
  Euro,
  AlertTriangle,
  Loader2,
} from "lucide-react";

function fmtInt(n) {
  return new Intl.NumberFormat("en-US").format(Math.round(n));
}
function fmtEur(n) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
}

export default function Dashboard() {
  const { selectedShopId, selectedShop } = useShop();
  const { t } = useLang();
  const qc = useQueryClient();

  const { data: overview, isLoading } = useQuery({
    queryKey: ["overview", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/overview`)).data,
    enabled: !!selectedShopId,
  });

  const rehealMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/shops/${selectedShopId}/reheal-mocks`)).data,
    onSuccess: (data) => {
      toast.success(
        data.message ||
          `${data.healed} von ${data.checked} Mock-Keywords erfolgreich via DataForSEO verifiziert.`,
      );
      // Delayed refetch since reheal runs in background
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["overview", selectedShopId] });
        qc.invalidateQueries({ queryKey: ["keywords", selectedShopId] });
      }, 30_000);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Reheal fehlgeschlagen"),
  });

  if (!selectedShopId) return null;

  const mockCount = overview?.mock_keywords_count ?? 0;
  const realTop10 = overview?.real_top_10_count ?? 0;
  const totalTop10 = overview?.top_10_count ?? 0;
  const hasMockDrift = mockCount > 0 && totalTop10 > realTop10;

  return (
    <div data-testid={DASH.overviewRoot} className="space-y-8 rk-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
            {t("nav_dashboard")} · {selectedShop?.industry}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {selectedShop?.name}
          </h1>
          <div className="text-sm font-mono text-muted-foreground mt-1">
            {selectedShop?.domain}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <RetestAllButton shopId={selectedShopId} />
          <div className="text-xs font-mono text-muted-foreground border border-border rounded-md px-3 py-2">
            {t("updated_at")} · {new Date().toISOString().slice(0, 10)}
          </div>
        </div>
      </header>

      {hasMockDrift && (
        <div
          className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
          data-testid="mock-drift-warning"
        >
          <div className="flex items-start gap-3 flex-1">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-amber-500">
                Achtung: {mockCount} Keywords zeigen unverifizierte Mock-Positionen
              </div>
              <div className="text-muted-foreground mt-1">
                Google-verifizierte Top-10:{" "}
                <span className="font-mono text-foreground">{realTop10}</span> ·
                Mit Mock-Daten angezeigt:{" "}
                <span className="font-mono text-foreground">{totalTop10}</span>. Die
                Differenz sind Alt-Daten, die DataForSEO noch nicht verifiziert hat.
              </div>
            </div>
          </div>
          <Button
            onClick={() => rehealMutation.mutate()}
            disabled={rehealMutation.isPending}
            variant="outline"
            className="border-amber-500/40 hover:bg-amber-500/20 gap-2 shrink-0"
            data-testid="reheal-mocks-btn"
          >
            {rehealMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Jetzt via DataForSEO verifizieren
          </Button>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 rk-stagger">
        <KpiCard
          testId={DASH.kpiRevenue}
          label={t("kpi_revenue")}
          value={overview ? fmtEur(overview.revenue_estimate_eur) : "—"}
          hint="/ month · organic"
          icon={Euro}
        />
        <KpiCard
          testId={DASH.kpiKeywords}
          label={t("kpi_keywords_tracked")}
          value={overview ? overview.keywords_tracked : "—"}
          hint={isLoading ? "…" : `${overview?.top_10_count ?? 0} · ${t("top_10")}`}
          icon={Target}
        />
        <KpiCard
          testId={DASH.kpiAvgPosition}
          label={t("kpi_avg_position")}
          value={overview ? overview.avg_position : "—"}
          hint={
            overview
              ? `${overview.positions_up} ↑ / ${overview.positions_down} ↓`
              : ""
          }
          icon={ArrowUpDown}
        />
        <KpiCard
          testId={DASH.kpiVisibility}
          label={t("kpi_visibility")}
          value={overview ? `${overview.visibility_score}` : "—"}
          hint={overview ? `${overview.top_3_count} · ${t("top_3")}` : ""}
          icon={Eye}
        />
        <KpiCard
          testId={DASH.kpiTraffic}
          label={t("kpi_traffic")}
          value={overview ? fmtInt(overview.organic_traffic_estimate) : "—"}
          hint="/ month"
          icon={Users}
        />
      </div>

      {/* GSC panel — real Google Search Console data if connected, else CTA */}
      <GscPanel />

      {/* Underperformer alerts */}
      <AlertsPanel />

      {/* Chart + Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-medium tracking-tight">
              {t("visibility_trend")}
            </h2>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              index 0–100
            </div>
          </div>
          {overview ? (
            <VisibilityChart data={overview.visibility_history} testId={DASH.visibilityChart} />
          ) : (
            <div className="h-64 grid place-items-center text-sm text-muted-foreground">…</div>
          )}
        </div>

        <div
          data-testid={DASH.positionDistribution}
          className="border border-border bg-card rounded-lg p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-medium tracking-tight">
              {t("position_distribution")}
            </h2>
          </div>
          <div className="space-y-4">
            {[
              { label: t("top_3"), value: overview?.top_3_count ?? 0, tone: "hsl(var(--chart-3))" },
              { label: t("top_10"), value: overview?.top_10_count ?? 0, tone: "hsl(var(--chart-1))" },
              { label: t("top_100"), value: overview?.top_100_count ?? 0, tone: "hsl(var(--chart-2))" },
            ].map((r) => {
              const total = overview?.keywords_tracked || 1;
              const pct = Math.round((r.value / total) * 100);
              return (
                <div key={r.label}>
                  <div className="flex items-center justify-between text-xs font-mono mb-1.5">
                    <span className="text-muted-foreground">{r.label}</span>
                    <span className="tabular">{r.value} · {pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, background: r.tone }}
                    />
                  </div>
                </div>
              );
            })}

            <div className="pt-4 mt-2 border-t border-border grid grid-cols-2 gap-3">
              <div className="border border-border rounded-md p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mb-1 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-[hsl(var(--chart-3))]" />
                  {t("positions_up")}
                </div>
                <div className="font-mono text-2xl tabular">
                  {overview?.positions_up ?? "—"}
                </div>
              </div>
              <div className="border border-border rounded-md p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mb-1 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3 text-destructive" />
                  {t("positions_down")}
                </div>
                <div className="font-mono text-2xl tabular">
                  {overview?.positions_down ?? "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
